import React, { useState, useEffect, useCallback } from 'react';
import { logger } from './lib/logger';
import { ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import supabase from './supabase';
import { calcGanttForBoxes, generateCalendarHeaders } from './types/ganttUtils';
import type { GanttTask, CalendarHeaders } from './types/ganttUtils';
import type { Box, Department } from './types';

interface GanttArrow { fromBox: number; toBox: number; }

interface ProjectGanttViewProps {
  projectId: string;
  projectName: string;
  user: { id: string; email?: string };
  onClose: () => void;
}

interface FlowchartWithData {
  id: string;
  title: string;
  boxes: Box[];
  arrows: GanttArrow[];
  departments?: Department[];
  color?: string; // project color or auto-assigned
}

const FC_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
];

const ROW_H   = 36;
const MIN_DW  = 20; // min px per unit

export default function ProjectGanttView({ projectId, projectName, user, onClose }: ProjectGanttViewProps) {
  const [flowcharts, setFlowcharts]   = useState<FlowchartWithData[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState<string | null>(null);
  const [collapsed, setCollapsed]     = useState<Record<string, boolean>>({});
  const [startDate, setStartDate]     = useState<Date>(new Date());
  const [durationUnit, setDurationUnit] = useState<'days' | 'weeks' | 'hours'>('days');
  const [workHoursOnly, setWorkHoursOnly] = useState(false);
  const [visibleLevels, setVisibleLevels] = useState({ quarters: true, months: true, weeks: true, days: true, hours: true });
  const [labelWidth, setLabelWidth]   = useState<number>(192);
  const [selectedGanttTask, setSelectedGanttTask] = useState<{ fcId: string; boxId: number } | null>(null);
  const [showAllDeps, setShowAllDeps] = useState<boolean>(false);

  // ── Column resize ────────────────────────────────────────────────────────
  const handleLabelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = labelWidth;

    const onMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      setLabelWidth(Math.max(100, Math.min(500, startWidth + delta)));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [labelWidth]);

  // ── Load flowcharts ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Get all flowcharts in this project that user has access to — exclude mindmaps
        const { data, error } = await supabase
          .from('flowcharts')
          .select('id, title, boxes, arrows, departments')
          .eq('project_id', projectId)
          .or('map_type.is.null,map_type.eq.flowchart')
          .is('deleted_at', null)
          .order('created_at', { ascending: true });

        if (error) throw error;

        const parsed: FlowchartWithData[] = (data || []).map((f, i) => {
          const rawArrows: unknown[] = Array.isArray(f.arrows) ? f.arrows : (typeof f.arrows === 'string' ? JSON.parse(f.arrows || '[]') : []);
          return {
            id: f.id,
            title: f.title || 'Untitled',
            boxes: Array.isArray(f.boxes) ? f.boxes : (typeof f.boxes === 'string' ? JSON.parse(f.boxes || '[]') : []),
            arrows: rawArrows.map((a): GanttArrow => { const r = a as Record<string, number>; return { fromBox: r.fromBox ?? r.from, toBox: r.toBox ?? r.to }; }),
            departments: Array.isArray(f.departments) ? f.departments : [],
            color: FC_COLORS[i % FC_COLORS.length],
          };
        });

        setFlowcharts(parsed);
      } catch (e) {
        logger.error('Error loading project flowcharts:', e);
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  // ── Compute tasks per flowchart ──────────────────────────────────────────
  const getTasksForFlowchart = useCallback((fc: FlowchartWithData): GanttTask[] => {
    try {
      return calcGanttForBoxes(fc.boxes, fc.arrows)
        .filter(t => t.type !== 'callout' && t.type !== 'startday');
    } catch (e) {
      logger.error(`Error computing tasks for flowchart ${fc.id}:`, e);
      return [];
    }
  }, []);

  // ── Global timeline range ─────────────────────────────────────────────
  const allTasks = flowcharts.flatMap(fc => getTasksForFlowchart(fc));
  const rawMax = allTasks.reduce((mx, t) => Math.max(mx, t.start + t.duration), 1);
  const globalMax = Math.ceil(isFinite(rawMax) && rawMax > 0 ? rawMax : 20);

  const dw = Math.max(MIN_DW, 800 / globalMax);
  const calendarHeaders = generateCalendarHeaders(globalMax, durationUnit, startDate, workHoursOnly);

  // ── Toggle collapse ──────────────────────────────────────────────────────
  const toggle = (id: string) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  if (loadError) {
    return (
      <div className="w-full h-screen bg-blue-50 flex flex-col">
        <GanttHeader title={projectName} onClose={onClose} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-red-600 max-w-md px-4">
            <p className="font-semibold mb-2">Error loading project Gantt</p>
            <p className="text-sm text-gray-600 font-mono bg-gray-100 p-2 rounded">{loadError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3" />
          <p className="text-gray-600">Loading project Gantt…</p>
        </div>
      </div>
    );
  }

  if (flowcharts.length === 0) {
    return (
      <div className="w-full h-screen bg-blue-50 flex flex-col">
        <GanttHeader title={projectName} onClose={onClose} />
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <p>No flowcharts found in this project.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen bg-blue-50 flex flex-col">
      {/* ── Toolbar ── */}
      <div className="bg-white shadow px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-orange-500" />
          <h1 className="text-lg font-bold text-gray-800">Project Gantt: {projectName}</h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Duration unit */}
          <div className="flex items-center gap-1 border rounded px-2 py-1 bg-gray-50 text-xs">
            <span className="font-semibold text-gray-600 mr-1">Unit:</span>
            {(['hours', 'days', 'weeks'] as const).map(u => (
              <button
                key={u}
                onClick={() => setDurationUnit(u)}
                className={`px-2 py-0.5 rounded font-medium transition-colors ${
                  durationUnit === u ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {u.charAt(0).toUpperCase() + u.slice(1)}
              </button>
            ))}
          </div>

          {/* Visible levels */}
          <div className="flex items-center gap-2 px-2 py-1 border rounded bg-gray-50 text-xs">
            <span className="font-semibold text-gray-600">Show:</span>
            {durationUnit === 'weeks' && (
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={visibleLevels.quarters} onChange={e => setVisibleLevels(v => ({...v, quarters: e.target.checked}))} />
                Quarters
              </label>
            )}
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={visibleLevels.months} onChange={e => setVisibleLevels(v => ({...v, months: e.target.checked}))} />
              Months
            </label>
            {(durationUnit === 'weeks' || durationUnit === 'days') && (
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={visibleLevels.weeks} onChange={e => setVisibleLevels(v => ({...v, weeks: e.target.checked}))} />
                Weeks
              </label>
            )}
            {(durationUnit === 'days' || durationUnit === 'hours') && (
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={visibleLevels.days} onChange={e => setVisibleLevels(v => ({...v, days: e.target.checked}))} />
                Days
              </label>
            )}
            {durationUnit === 'hours' && (
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={visibleLevels.hours} onChange={e => setVisibleLevels(v => ({...v, hours: e.target.checked}))} />
                Hours
              </label>
            )}
          </div>

          {/* Work hours only */}
          {(durationUnit === 'days' || durationUnit === 'hours') && (
            <label className="flex items-center gap-1 border rounded px-2 py-1 bg-gray-50 text-xs cursor-pointer font-semibold">
              <input type="checkbox" checked={workHoursOnly} onChange={e => setWorkHoursOnly(e.target.checked)} />
              Work hours only
            </label>
          )}

          {/* Show all dependencies toggle */}
          <label className="flex items-center gap-2 px-2 py-1 border rounded bg-gray-50 text-xs cursor-pointer font-semibold select-none">
            <input
              type="checkbox"
              checked={showAllDeps}
              onChange={e => setShowAllDeps(e.target.checked)}
              className="cursor-pointer"
            />
            Show all dependencies
          </label>

          {/* Start date */}
          <input
            type="date"
            value={startDate.toISOString().split('T')[0]}
            onChange={e => setStartDate(new Date(e.target.value))}
            className="px-2 py-1 border rounded text-xs"
          />

<button onClick={onClose} className="bg-blue-500 text-white px-3 py-1 rounded text-sm">
            Back to Flowchart
          </button>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-white rounded-lg shadow" style={{ position: 'relative' }}>

          {/* ── Kolombreedte resize handle (volledige hoogte) ── */}
          <div
            style={{
              position: 'absolute',
              left: labelWidth - 3,
              top: 0,
              bottom: 0,
              width: 6,
              cursor: 'col-resize',
              zIndex: 30,
            }}
            onMouseDown={handleLabelResizeStart}
          />

          {/* ── Sticky calendar header ── */}
          <div className="sticky top-0 z-10 bg-white border-b-2 border-gray-300">
            <CalendarHeader
              calendarHeaders={calendarHeaders}
              durationUnit={durationUnit}
              visibleLevels={visibleLevels}
              dw={dw}
              labelWidth={labelWidth}
              onLabelResizeStart={handleLabelResizeStart}
            />
          </div>

          {/* ── Flowchart sections ── */}
          {flowcharts.map((fc) => {
            const tasks = getTasksForFlowchart(fc);
            const isCollapsed = collapsed[fc.id] ?? false;

            return (
              <div key={fc.id} className="border-b border-gray-200 last:border-0">
                {/* Flowchart header row */}
                <div
                  className="flex items-center sticky top-0 z-10"
                  style={{
                    background: `${fc.color}18`, // very light tint
                    borderLeft: `4px solid ${fc.color}`,
                  }}
                >
                  {/* Collapse toggle + title */}
                  <div
                    className="flex items-center gap-2 cursor-pointer select-none py-2 px-3"
                    style={{ width: labelWidth, flexShrink: 0 }}
                    onClick={() => toggle(fc.id)}
                  >
                    <span style={{ color: fc.color }}>
                      {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                    </span>
                    <span className="text-sm font-semibold text-gray-800 truncate" title={fc.title}>
                      {fc.title}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                      {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Header bar spanning full timeline */}
                  <div className="flex-1 relative" style={{ height: ROW_H }}>
                    <div
                      className="absolute top-1/2 -translate-y-1/2 rounded opacity-30"
                      style={{
                        left: 0,
                        right: 0,
                        height: 4,
                        background: fc.color,
                      }}
                    />
                    {/* Summary bar: span from first task start to last task end */}
                    {tasks.length > 0 && (() => {
                      const firstStart = Math.min(...tasks.map(t => t.start));
                      const lastEnd    = Math.max(...tasks.map(t => t.start + t.duration));
                      return (
                        <div
                          className="absolute top-1/2 -translate-y-1/2 rounded opacity-50"
                          style={{
                            left: firstStart * dw,
                            width: (lastEnd - firstStart) * dw,
                            height: 8,
                            background: fc.color,
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>

                {/* Task rows */}
                {!isCollapsed && (() => {
                  const ROW_TOTAL = ROW_H + 8;
                  const ROW_CENTER = ROW_TOTAL / 2;
                  const fcArrows = fc.arrows || [];
                  const isSelected = (boxId: number) => selectedGanttTask?.fcId === fc.id && selectedGanttTask?.boxId === boxId;
                  const getCoords = (boxId: number) => {
                    const task = tasks.find(t2 => t2.id === boxId);
                    const rowIdx = tasks.findIndex(t2 => t2.id === boxId);
                    if (!task || rowIdx === -1) return null;
                    return {
                      leftX: task.start * dw,
                      rightX: Math.max(task.start * dw + task.duration * dw - 2, 4),
                      y: rowIdx * ROW_TOTAL + ROW_CENTER,
                    };
                  };
                  const makePath = (x1: number, y1: number, x2: number, y2: number) => {
                    const cx = Math.max(40, Math.abs(x2 - x1) / 2);
                    return `M ${x1} ${y1} C ${x1 + cx} ${y1} ${x2 - cx} ${y2} ${x2} ${y2}`;
                  };
                  const selBoxId = selectedGanttTask?.fcId === fc.id ? selectedGanttTask.boxId : null;
                  const predArrows = selBoxId !== null ? fcArrows.filter(a => a.toBox === selBoxId) : [];
                  const succArrows = selBoxId !== null ? fcArrows.filter(a => a.fromBox === selBoxId) : [];
                  const selectedArrowKeys = new Set([...predArrows, ...succArrows].map(a => `${a.fromBox}-${a.toBox}`));
                  return (<div style={{ position: 'relative' }}>
                    {/* SVG dependency overlay */}
                    {(showAllDeps || selBoxId !== null) && (
                      <svg
                        style={{ position: 'absolute', top: 0, left: labelWidth, pointerEvents: 'none', overflow: 'visible', zIndex: 10 }}
                        width={globalMax * dw + 40}
                        height={tasks.length * ROW_TOTAL}
                      >
                        <defs>
                          <marker id={`ag-${fc.id}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                            <path d="M0,0 L0,6 L8,3 z" fill="#94a3b8" />
                          </marker>
                          <marker id={`ap-${fc.id}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                            <path d="M0,0 L0,6 L8,3 z" fill="#3b82f6" />
                          </marker>
                          <marker id={`as-${fc.id}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                            <path d="M0,0 L0,6 L8,3 z" fill="#f59e0b" />
                          </marker>
                        </defs>
                        {/* All arrows in gray (skip highlighted ones) */}
                        {showAllDeps && fcArrows.filter(a => !selectedArrowKeys.has(`${a.fromBox}-${a.toBox}`)).map((a, ai) => {
                          const from = getCoords(a.fromBox); const to = getCoords(a.toBox);
                          if (!from || !to) return null;
                          return <path key={`g-${ai}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#94a3b8" strokeWidth="1.5" markerEnd={`url(#ag-${fc.id})`} />;
                        })}
                        {predArrows.map((a, ai) => {
                          const from = getCoords(a.fromBox); const to = getCoords(a.toBox);
                          if (!from || !to) return null;
                          return <path key={`p-${ai}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#3b82f6" strokeWidth="2" markerEnd={`url(#ap-${fc.id})`} />;
                        })}
                        {succArrows.map((a, ai) => {
                          const from = getCoords(a.fromBox); const to = getCoords(a.toBox);
                          if (!from || !to) return null;
                          return <path key={`s-${ai}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#f59e0b" strokeWidth="2" markerEnd={`url(#as-${fc.id})`} />;
                        })}
                      </svg>
                    )}
                  {tasks.map(t => (
                  <div key={t.id} className="flex items-center hover:bg-gray-50" style={{ height: ROW_H + 8 }}>
                    {/* Label */}
                    <div
                      className="text-xs text-gray-700 truncate pl-8 pr-2"
                      style={{ width: labelWidth, flexShrink: 0 }}
                      title={t.text}
                    >
                      {t.text}
                      {t.assignee && (
                        <span className="ml-1 text-gray-400">· {t.assignee}</span>
                      )}
                    </div>

                    {/* Bar area */}
                    <div className="flex-1 relative" style={{ height: ROW_H + 8 }}>
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {[...Array(globalMax)].map((_, i) => (
                          <div key={i} className="border-l border-gray-100" style={{ width: dw, flexShrink: 0 }} />
                        ))}
                      </div>

                      {/* Task bar */}
                      <div
                        onClick={() => setSelectedGanttTask(isSelected(t.id) ? null : { fcId: fc.id, boxId: t.id })}
                        className="absolute top-1/2 -translate-y-1/2 rounded shadow-sm overflow-hidden"
                        style={{
                          left: t.start * dw,
                          width: Math.max(t.duration * dw - 2, 4),
                          height: ROW_H - 4,
                          backgroundColor: t.departmentColor || fc.color,
                          minWidth: 4,
                          cursor: 'pointer',
                          outline: isSelected(t.id) ? '3px solid #1d4ed8' : undefined,
                          outlineOffset: '2px',
                        }}
                      >
                        {/* Completion overlay */}
                        {(t.completion || 0) > 0 && (
                          <div
                            className="absolute top-0 left-0 h-full"
                            style={{
                              width: `${t.completion}%`,
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              borderRight: (t.completion ?? 0) < 100 ? '2px solid rgba(0,0,0,0.3)' : 'none',
                            }}
                          />
                        )}
                        {/* Duration label */}
                        {t.duration * dw > 30 && (
                          <div className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-semibold drop-shadow">
                            {t.duration}{durationUnit === 'hours' ? 'h' : durationUnit === 'weeks' ? 'w' : 'd'}
                            {(t.completion || 0) > 0 && <span className="ml-0.5 opacity-80">({t.completion}%)</span>}
                          </div>
                        )}
                      </div>

                      {/* Milestone diamond */}
                      {t.importantFinish && (
                        <div
                          className="absolute top-1/2 -translate-y-1/2 bg-purple-500"
                          style={{
                            left: (t.start + t.duration) * dw - 6,
                            width: 12,
                            height: 12,
                            transform: 'translateY(-50%) rotate(45deg)',
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
                  </div>);
                })()}

                {/* Empty state */}
                {!isCollapsed && tasks.length === 0 && (
                  <div className="flex items-center text-xs text-gray-400 italic pl-8 py-2">
                    No tasks with duration — add durations in Advanced mode
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Calendar header component ────────────────────────────────────────────────
function CalendarHeader({
  calendarHeaders, durationUnit, visibleLevels, dw, labelWidth, onLabelResizeStart,
}: {
  calendarHeaders: CalendarHeaders;
  durationUnit: string;
  visibleLevels: Record<string, boolean>;
  dw: number;
  labelWidth: number;
  onLabelResizeStart: (e: React.MouseEvent) => void;
}) {
  const LabelSpacer = () => <div style={{ width: labelWidth, flexShrink: 0 }} />;

  const TaskLabel = () => (
    <div
      style={{ width: labelWidth, flexShrink: 0, position: 'relative' }}
      className="text-xs font-semibold text-right pr-2 flex items-center justify-end text-gray-600"
    >
      Task
      <div
        style={{
          position: 'absolute', right: -3, top: 0, bottom: 0, width: 6,
          cursor: 'col-resize', zIndex: 20,
        }}
        className="hover:bg-blue-400 hover:opacity-50"
        onMouseDown={onLabelResizeStart}
      />
    </div>
  );

  if (durationUnit === 'weeks') {
    return (
      <>
        {visibleLevels.quarters && calendarHeaders.quarters && calendarHeaders.quarters.length > 0 && (
          <div className="flex">
            <LabelSpacer />
            <div className="flex-1 flex border-b border-gray-300">
              {calendarHeaders.quarters.map((q, i) => (
                <div key={i} className={`text-xs font-bold text-center py-1.5 border-r border-gray-300 truncate ${i % 2 === 0 ? 'bg-blue-50' : 'bg-blue-100'}`} style={{ width: dw * q.span }}>{q.label}</div>
              ))}
            </div>
          </div>
        )}
        {visibleLevels.months && calendarHeaders.months && calendarHeaders.months.length > 0 && (
          <div className="flex">
            <LabelSpacer />
            <div className="flex-1 flex border-b border-gray-200">
              {calendarHeaders.months.map((m, i) => (
                <div key={i} className={`text-xs font-semibold text-center py-1 border-r border-gray-200 truncate ${i % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}`} style={{ width: dw * m.span }}>{m.label}</div>
              ))}
            </div>
          </div>
        )}
        {visibleLevels.weeks && calendarHeaders.weeks && calendarHeaders.weeks.length > 0 && (
          <div className="flex border-b-2 border-gray-400">
            <TaskLabel />
            <div className="flex-1 flex">
              {(calendarHeaders.weeks as { quarter: string; month: string; week: number }[]).map((w, i) => (
                <div key={i} className="text-xs text-center text-gray-500 py-1 border-r border-gray-200" style={{ width: dw }}>{w.week}</div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }

  if (durationUnit === 'days') {
    return (
      <>
        {visibleLevels.months && calendarHeaders.months && calendarHeaders.months.length > 0 && (
          <div className="flex">
            <LabelSpacer />
            <div className="flex-1 flex border-b border-gray-300">
              {calendarHeaders.months.map((m, i) => (
                <div key={i} className={`text-xs font-bold text-center py-1.5 border-r border-gray-300 truncate ${i % 2 === 0 ? 'bg-blue-50' : 'bg-blue-100'}`} style={{ width: dw * m.span }}>{m.label}</div>
              ))}
            </div>
          </div>
        )}
        {visibleLevels.weeks && calendarHeaders.weeks && calendarHeaders.weeks.length > 0 && (
          <div className="flex">
            <LabelSpacer />
            <div className="flex-1 flex border-b border-gray-200">
              {(calendarHeaders.weeks as { label: string; span: number }[]).map((w, i) => (
                <div key={i} className={`text-xs font-semibold text-center py-1 border-r border-gray-200 truncate ${i % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}`} style={{ width: dw * w.span }}>{w.label}</div>
              ))}
            </div>
          </div>
        )}
        {visibleLevels.days && calendarHeaders.days && calendarHeaders.days.length > 0 && (
          <div className="flex border-b-2 border-gray-400">
            <TaskLabel />
            <div className="flex-1 flex">
              {(calendarHeaders.days as { month: string; week: number; day: number; dayName: string; isWeekday: boolean }[]).map((d, i) => (
                <div key={i} className={`text-xs text-center py-1 border-r border-gray-200 ${d.isWeekday ? 'bg-white text-gray-700' : 'bg-gray-100 text-gray-400'}`} style={{ width: dw }}>
                  <div className="text-[9px]">{d.dayName}</div>
                  <div className="font-semibold">{d.day}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }

  // hours
  return (
    <>
      {visibleLevels.months && calendarHeaders.months && calendarHeaders.months.length > 0 && (
        <div className="flex">
          <LabelSpacer />
          <div className="flex-1 flex border-b border-gray-300">
            {calendarHeaders.months.map((m, i) => (
              <div key={i} className={`text-xs font-bold text-center py-1.5 border-r border-gray-300 truncate ${i % 2 === 0 ? 'bg-blue-50' : 'bg-blue-100'}`} style={{ width: dw * m.span }}>{m.label}</div>
            ))}
          </div>
        </div>
      )}
      {visibleLevels.days && calendarHeaders.days && calendarHeaders.days.length > 0 && (
        <div className="flex">
          <LabelSpacer />
          <div className="flex-1 flex border-b border-gray-200">
            {(calendarHeaders.days as { label: string; span: number }[]).map((d, i) => (
              <div key={i} className={`text-xs font-semibold text-center py-1 border-r border-gray-200 truncate ${i % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}`} style={{ width: dw * d.span }}>{d.label}</div>
            ))}
          </div>
        </div>
      )}
      {visibleLevels.hours && calendarHeaders.hours && calendarHeaders.hours.length > 0 && (
        <div className="flex border-b-2 border-gray-400">
          <TaskLabel />
          <div className="flex-1 flex">
            {calendarHeaders.hours.map((h, i) => (
              <div key={i} className={`text-xs text-center py-1 border-r border-gray-200 ${h.isWorkingHour ? 'bg-white text-gray-700' : 'bg-gray-100 text-gray-400'}`} style={{ width: dw }}>
                {h.hour.split(':')[0]}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function GanttHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="bg-white shadow px-4 py-3 flex items-center justify-between">
      <h1 className="text-xl font-bold text-gray-800">Project Gantt: {title}</h1>
      <button onClick={onClose} className="bg-blue-500 text-white px-3 py-1 rounded text-sm">
        Back to Flowchart
      </button>
    </div>
  );
}
